import React, { useState } from 'react';
import { ChevronDown, ChevronRight, Plus, Pencil, Trash2, Clock, CreditCard, Landmark, Link, GripVertical } from 'lucide-react';
import { Droppable, Draggable, DraggableProvidedDragHandleProps } from '@hello-pangea/dnd';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CategoryWithMonthData } from '../types/category';
import { SubCategoryRow } from './SubCategoryRow';
import { usePlans } from '../contexts/PlanContext';
import { useAccounts } from '../contexts/AccountContext';
import { useUserPreferences } from '../contexts/UserPreferencesContext';

interface CategoryGroupProps {
  category: CategoryWithMonthData;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onAddSubCategory: () => void;
  onEditGroup: () => void;
  onEditSubCategory: (subCategory: CategoryWithMonthData) => void;
  onDeleteGroup: () => void;
  onDeleteSubCategory: (subCategory: CategoryWithMonthData) => void;
  dragHandleProps?: DraggableProvidedDragHandleProps | null;
  isDragging?: boolean;
}

// PERFORMANCE: Memoize CategoryGroup to prevent unnecessary re-renders
export const CategoryGroup: React.FC<CategoryGroupProps> = React.memo(({
  category,
  isExpanded,
  onToggleExpand,
  onAddSubCategory,
  onEditGroup,
  onEditSubCategory,
  onDeleteGroup,
  onDeleteSubCategory,
  dragHandleProps,
  isDragging = false,
}) => {
  const { t } = useTranslation();
  const navigateRouter = useNavigate();
  const { currentMonthPlan } = usePlans();
  const { accounts } = useAccounts();
  const { formatMoney } = useUserPreferences();

  // Toggle between showing transaction or balance
  const [showBalance, setShowBalance] = useState(false);

  // Navigate to transaction page with filters for parent category
  const handleViewTransactions = () => {
    if (!currentMonthPlan) return;

    let dateFrom: Date;
    let dateTo: Date;

    // Check if this is a CC/Paylater system group - use extended billing cycle dates
    if (category.is_system_group && category.linked_account_id) {
      const linkedAccount = accounts.find(acc => acc.id === category.linked_account_id);

      if (linkedAccount && ['credit_card', 'paylater'].includes(linkedAccount.type) && linkedAccount.billing_date) {
        // Parse month string directly
        const [year, month] = currentMonthPlan.month.split('-').map(Number);

        // Extended range: previous billing date to NEXT month's billing date
        // This accounts for payments made after the billing date
        const previousBillingDate = new Date(year, month - 2, linkedAccount.billing_date);
        const nextBillingDate = new Date(year, month, linkedAccount.billing_date); // Next month

        dateFrom = previousBillingDate;
        dateTo = nextBillingDate;
      } else {
        // Fallback to normal month dates if account not found or no billing date
        const [yearStr, monthStr] = currentMonthPlan.month.split('-');
        const year = parseInt(yearStr);
        const month = parseInt(monthStr);

        // Get last day of the month
        const lastDay = new Date(year, month, 0).getDate();

        dateFrom = new Date(year, month - 1, lastDay);
        dateFrom.setDate(dateFrom.getDate() - 60);
        dateTo = new Date(year, month - 1, lastDay);
      }
    } else {
      // Normal category - get last day of the month
      const [yearStr, monthStr] = currentMonthPlan.month.split('-');
      const year = parseInt(yearStr);
      const month = parseInt(monthStr);

      // Get last day of the month
      const lastDay = new Date(year, month, 0).getDate();

      dateFrom = new Date(year, month - 1, lastDay);
      dateFrom.setDate(dateFrom.getDate() - 60);
      dateTo = new Date(year, month - 1, lastDay);
    }

    // Format dates as YYYY-MM-DD without timezone conversion
    const formatDate = (date: Date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    navigateRouter('/history', {
      state: {
        parentCategoryId: category.id,
        monthPlanId: currentMonthPlan.id, // Pass month plan ID for Anggaran Bulan filter
        dateFrom: formatDate(dateFrom),
        dateTo: formatDate(dateTo),
      }
    });
  };

  // Calculate totals from children
  // For goals with target months, use monthly target instead of full goal target
  const totalAllocated = category.children.reduce((sum, child) => {
    if (child.is_goal && child.goal_monthly_target !== null && child.goal_monthly_target !== undefined) {
      // Goal with target month: use monthly target for this month's allocation
      return sum + (child.goal_monthly_target || 0);
    }
    // Non-goal or goal without target month: use allocated amount
    return sum + (child.allocated || 0);
  }, 0);

  const totalTransaction = category.children.reduce((sum, child) => sum + (child.transaction || 0), 0);
  const totalBalance = totalAllocated - totalTransaction;

  // Calculate progress (capped at 100% for display)
  const progressPercentage = totalAllocated > 0 ? (totalTransaction / totalAllocated) * 100 : 0;

  // Calculate overspending from non-goal categories only
  const nonGoalOverspending = category.children.reduce((sum, child) => {
    if (child.is_goal) return sum; // Goals don't overspend, they oversave
    const overspent = Math.max(0, (child.transaction || 0) - (child.allocated || 0));
    return sum + overspent;
  }, 0);

  // Check if there's any actual overspending (from non-goal categories)
  const hasOverspending = nonGoalOverspending > 0;

  const getProgressColor = () => {
    // Use hasOverspending instead of isOverspent to account for goal oversaving
    if (hasOverspending) return 'bg-red-500';
    return 'bg-emerald-500';
  };

  // Helper function to create diagonal line pattern
  const getDiagonalLinesStyle = (baseColor: string) => {
    return {
      backgroundImage: `repeating-linear-gradient(
        45deg,
        ${baseColor},
        ${baseColor} 2px,
        transparent 2px,
        transparent 4px
      )`
    };
  };

  // Container background color (grey when empty, white otherwise)
  const containerBgColor = totalTransaction === 0 ? 'bg-gray-100' : 'bg-white';

  // Check if bar is full (100%) but not overspent (use hasOverspending for goal-aware check)
  const isFull = progressPercentage >= 100 && !hasOverspending;

  // Check if this is a system group (CC or Loan) for special labels
  const isSystemGroup = category.is_system_group === true;
  const transactionLabel = isSystemGroup ? t('category_group.payment') : t('category_group.transaction');
  const balanceLabel = isSystemGroup ? t('category_group.bill') : t('category_group.remaining');

  // Get linked account info for system groups
  const linkedAccount = isSystemGroup && category.linked_account_id
    ? accounts.find(acc => acc.id === category.linked_account_id)
    : null;
  const isCreditCard = linkedAccount?.type === 'credit_card';
  const isLoan = linkedAccount?.type === 'loan';

  // Check if group is orphaned (system group but no linked account)
  const isOrphaned = isSystemGroup && !category.linked_account_id;

  // Tooltip state for link icon
  const [showLinkTooltip, setShowLinkTooltip] = useState(false);

  // Get icon for system group
  const getSystemGroupIcon = () => {
    if (isCreditCard) return <CreditCard className="w-4 h-4" />;
    if (isLoan) return <Landmark className="w-4 h-4" />;
    return null;
  };

  // Get empty state message
  const getEmptyMessage = () => {
    if (isCreditCard) return t('category_group.empty_cc');
    if (isLoan) return t('category_group.empty_loan');
    return t('category_group.empty_default');
  };

  // Get background color - use custom yellow #f5f7c1 for system groups
  const backgroundStyle = isSystemGroup
    ? { background: 'linear-gradient(to right, #f5f7c1, #f5f7c1)' }
    : undefined;

  return (
    <div
      className={`bg-white border-2 border-[#077f5d] ${isDragging ? 'opacity-50' : ''}`}
      style={{
        boxShadow: '4px 4px 0 #077f5d',
        transition: 'none'
      }}
    >
      {/* Group Header - Mobile Focused */}
      <div className={`${isSystemGroup ? '' : 'bg-[#a9edba]'} border-b-2 border-[#077f5d] p-2`} style={isSystemGroup ? backgroundStyle : undefined}>
        {/* First row: Drag Handle, Chevron, Name, and Action buttons */}
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-1 flex-1">
            {/* Drag Handle - Hidden on desktop (md and above) */}
            <div
              {...dragHandleProps}
              className="md:hidden cursor-grab active:cursor-grabbing p-1 hover:bg-white flex-shrink-0 border-2 border-transparent hover:border-[#077f5d]"
              style={{ transition: 'none' }}
            >
              <GripVertical size={16} className="text-[#077f5d]" />
            </div>
            <button
              onClick={onToggleExpand}
              className="p-1 hover:bg-white flex-shrink-0 border-2 border-transparent hover:border-[#077f5d]"
              style={{ transition: 'none' }}
            >
              {isExpanded ? (
                <ChevronDown size={18} className="text-[#077f5d]" />
              ) : (
                <ChevronRight size={18} className="text-[#077f5d]" />
              )}
            </button>
            {isSystemGroup && getSystemGroupIcon()}
            <h3
              className="font-bold text-black uppercase text-left"
              style={{
                fontFamily: "'Press Start 2P', monospace",
                fontSize: '10px',
                lineHeight: '1.4'
              }}
            >
              {category.name}
            </h3>
            {/* Link icon for linked system groups */}
            {linkedAccount && (
              <>
                <button
                  ref={(el) => {
                    if (el && showLinkTooltip) {
                      const rect = el.getBoundingClientRect();
                      const tooltipEl = document.getElementById(`tooltip-${category.id}`);
                      if (tooltipEl) {
                        tooltipEl.style.left = `${rect.left + rect.width / 2}px`;
                        tooltipEl.style.top = `${rect.top - 8}px`;
                      }
                    }
                  }}
                  onMouseEnter={() => setShowLinkTooltip(true)}
                  onMouseLeave={() => setShowLinkTooltip(false)}
                  onClick={() => setShowLinkTooltip(!showLinkTooltip)}
                  className="p-0.5 text-emerald-600 hover:text-emerald-700 transition-colors"
                  type="button"
                >
                  <Link size={14} strokeWidth={3} />
                </button>
                {showLinkTooltip && (
                  <div
                    id={`tooltip-${category.id}`}
                    className="fixed -translate-x-1/2 -translate-y-full z-[9999] px-3 py-1.5 bg-black text-white text-xs rounded-md whitespace-nowrap shadow-xl pointer-events-none"
                  >
                    {t('category_group.linked_to')} {linkedAccount.name}
                    <div className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-black"></div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Right: Action buttons */}
          <div className="flex gap-1 flex-shrink-0">
            <button
              onClick={onAddSubCategory}
              className="p-0.5 text-[#077f5d] hover:bg-white border-2 border-transparent hover:border-[#077f5d]"
              style={{ transition: 'none' }}
              title={t('category_group.add_category')}
            >
              <Plus size={16} />
            </button>
            <button
              onClick={handleViewTransactions}
              className="p-0.5 text-[#077f5d] hover:bg-white border-2 border-transparent hover:border-[#077f5d]"
              style={{ transition: 'none' }}
              title={t('category_group.view_transactions')}
            >
              <Clock size={14} />
            </button>
            {/* Hide Edit and Delete buttons only for LINKED system groups */}
            {/* Show them for normal groups OR orphaned system groups */}
            {(!isSystemGroup || isOrphaned) && (
              <>
                <button
                  onClick={onEditGroup}
                  className="p-0.5 text-[#077f5d] hover:bg-white border-2 border-transparent hover:border-[#077f5d]"
                  style={{ transition: 'none' }}
                  title={t('category_group.edit_group')}
                >
                  <Pencil size={14} />
                </button>
                <button
                  onClick={onDeleteGroup}
                  className="p-0.5 text-[#077f5d] hover:bg-white hover:text-red-600 border-2 border-transparent hover:border-[#077f5d]"
                  style={{ transition: 'none' }}
                  title={t('category_group.delete_group')}
                >
                  <Trash2 size={14} />
                </button>
              </>
            )}
          </div>
        </div>

        {/* Second row: Transaction/Balance (left) and Allocated (right) - only show when collapsed */}
        {!isExpanded && (
          <div className="flex items-center justify-between mb-2">
            <button
              onClick={() => setShowBalance(!showBalance)}
              className="text-xs hover:text-[#3cbe8c] cursor-pointer font-mono text-left flex-1"
              style={{ transition: 'none' }}
            >
              {showBalance ? (
                <span className={totalBalance < 0 ? 'text-red-600 font-bold' : 'text-[#1a1a1a]'}>
                  {balanceLabel}: {formatMoney(totalBalance)}
                </span>
              ) : (
                <span className={hasOverspending ? 'text-red-600 font-bold' : 'text-[#1a1a1a]'}>
                  {transactionLabel}: {formatMoney(totalTransaction)}
                </span>
              )}
            </button>
            {/* Allocated (right side, same line) */}
            <div
              className="inline-flex items-center bg-[#dffbd0] text-[#077f5d] px-2 py-0 border-2 border-[#077f5d] font-mono font-bold"
              style={{
                fontSize: '14px'
              }}
            >
              {formatMoney(totalAllocated)}
            </div>
          </div>
        )}

        {/* Third row: Progress bar - only show when collapsed */}
        {!isExpanded && (
          <div className={`h-3 ${containerBgColor} border-2 border-[#077f5d] flex`}>
            {hasOverspending ? (
              // Has overspending from non-goal categories: Two segments inside the bar
              // Green segment: everything (including goal oversaving) EXCEPT non-goal overspending
              // Red segment: non-goal overspending only
              <>
                <div
                  className="h-full bg-emerald-500"
                  style={{
                    width: `${((totalTransaction - nonGoalOverspending) / totalTransaction) * 100}%`,
                    transition: 'none',
                    ...getDiagonalLinesStyle('#077f5d')
                  }}
                />
                <div
                  className="h-full bg-[#bc2222ff]"
                  style={{
                    width: `${(nonGoalOverspending / totalTransaction) * 100}%`,
                    transition: 'none',
                    ...getDiagonalLinesStyle('#991b1b')
                  }}
                />
              </>
            ) : isFull ? (
              // Full bar (100%) with no overspending: Green with dark green diagonal lines
              <div
                className="h-full bg-emerald-500"
                style={{
                  width: '100%',
                  transition: 'none',
                  ...getDiagonalLinesStyle('#077f5d')
                }}
              />
            ) : (
              // Normal progress: Keep current behavior
              <div
                className={`h-full ${getProgressColor()}`}
                style={{ width: `${Math.min(progressPercentage, 100)}%`, transition: 'none' }}
              />
            )}
          </div>
        )}
      </div>

      {/* Sub-categories */}
      {isExpanded && (
        <Droppable droppableId={`subcategories-${category.id}`} type={`subcategory-${category.id}`}>
          {(provided) => (
            <div
              ref={provided.innerRef}
              {...provided.droppableProps}
              className="divide-y-2 divide-[#cdf7bd]"
            >
              {category.children.length === 0 ? (
                <div className="p-8 text-center">
                  <p className="text-[#1a1a1a] font-mono text-sm mb-3">{getEmptyMessage()}</p>
                  <button
                    onClick={onAddSubCategory}
                    className="text-[#077f5d] hover:text-base text-sm font-mono font-bold"
                    style={{ transition: 'none' }}
                  >
                    + {t('category_group.add_category')}
                  </button>
                </div>
              ) : (
                category.children.map((child, index) => {
                  // Get billing date and account ID if this is a CC/Paylater system group
                  let billingDate: number | undefined;
                  let accountId: string | undefined;
                  if (isSystemGroup && category.linked_account_id) {
                    const linkedAccount = accounts.find(acc => acc.id === category.linked_account_id);
                    if (linkedAccount && ['credit_card', 'paylater'].includes(linkedAccount.type)) {
                      billingDate = linkedAccount.billing_date;
                      accountId = category.linked_account_id;
                    }
                  }

                  return (
                    <Draggable
                      key={child.id}
                      draggableId={child.id}
                      index={index}
                    >
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                        >
                          <SubCategoryRow
                            category={child}
                            onEdit={() => onEditSubCategory(child)}
                            onDelete={() => onDeleteSubCategory(child)}
                            isUnderSystemGroup={isSystemGroup}
                            linkedAccountBillingDate={billingDate}
                            linkedAccountId={accountId}
                            dragHandleProps={provided.dragHandleProps}
                            isDragging={snapshot.isDragging}
                          />
                        </div>
                      )}
                    </Draggable>
                  );
                })
              )}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      )}
    </div>
  );
}); // End React.memo
